import { unwrapResult } from "../../../helpers/apiResponse";
import { addMonths, dayToIso, endOfMonth, startOfMonth } from "./dayMath";
import {
  applyDelta,
  applyPage,
  clearInflight,
  createPlannerData,
  getAllocation,
  markInflight,
  patchAllocation,
  replaceResources,
} from "./plannerData";
import { LIMITS, planCoverage, runToDays, tilesCovering, tilesToHold, toRuns } from "./tiles";
import { tileOf } from "./timelineGeometry";

/** Request kinds in the order they are served: the user is waiting for a page, not for a tile of data. */
const PRIORITY = { page: 0, refresh: 1, delta: 2 };

const DEFAULTS = {
  pageSize: 20,
  /** Largest number of resources a refresh asks for in one request. */
  refreshSize: 50,
  /** Viewport changes are acted on at most this often while scrolling goes on. */
  coverageThrottleMs: 150,
  /** Data requests are paced: a burst of this many, then one per `1 / tokensPerSecond` seconds (twelve a minute). */
  tokenCapacity: 4,
  tokensPerSecond: 0.2,
  /** How long to wait before asking again after a request for data failed. */
  failureRetryMs: 5000,
};

const toIso = ({ startDay, endDay }) => ({ startDate: dayToIso(startDay), endDate: dayToIso(endDay) });

/**
 * Loads and keeps the planner's data.
 *
 * It starts with a page of resources and then follows the viewport: for the resources on screen it works out which
 * tiles of time they are missing and fetches only those, from the server's delta route, never asking again for a
 * tile it holds or is already fetching. Requests go one at a time, paced so scrolling cannot exceed what the server
 * allows, and any answer that belongs to filters that have since changed is dropped.
 *
 * The controller owns no timers or network of its own: the store, the api, the clock and the timers are passed in,
 * which is what lets tests run it against a fake server in simulated time.
 *
 * @param store  holds `{ data, status, error, isLoadingMore, hasMore, totalCount, pageNumber, filters, epoch }`
 * @param api  `{ getPage(params, signal), getAllocations(body, signal), updateAllocation(taskId, resourceId, body) }`
 * @param timeline  the span of days the planner can show
 * @param today  the current day, for the months a first page always includes
 * @param notify  `{ error(message), conflicts(conflicts), saved() }`
 * @param isCanceled  tells an aborted request from a failed one
 * @param retryAfterSeconds  the wait a 429 asks for, or null for any other error
 */
export function createPlannerController({
  store,
  api,
  timeline,
  today,
  notify = {},
  isCanceled = () => false,
  retryAfterSeconds = () => null,
  options = {},
  clock = () => Date.now(),
  timers = { setTimeout: (...args) => setTimeout(...args), clearTimeout: (...args) => clearTimeout(...args) },
}) {
  const settings = { ...DEFAULTS, ...options };

  let epoch = 0;
  let disposed = false;
  let viewport = null;
  let queue = [];
  let pumping = false;
  let runningController = null;
  let tokens = settings.tokenCapacity;
  let lastRefill = clock();
  let blockedUntil = 0;
  let coverageTimer = null;
  let lastCoverageAt = -Infinity;
  let failureNotified = false;
  let idleWaiters = [];
  let sleeper = null;
  const optimisticEdits = new Map();
  /** Resources whose data may be out of date: they are refreshed when they come into view. */
  const stale = new Set();

  const stats = { page: 0, refresh: 0, delta: 0, discarded: 0 };

  const initialState = () => ({
    data: createPlannerData(),
    status: "idle",
    error: null,
    isLoadingMore: false,
    hasMore: false,
    totalCount: 0,
    pageNumber: 0,
    filters: { projectId: null, resourceIds: [] },
    epoch: 0,
  });

  store.setState((state) => ({ ...initialState(), ...state }));

  const get = () => store.getState();
  const set = (patch) => store.setState(patch);
  const setData = (update) => store.setState((state) => ({ ...state, data: update(state.data) }));

  // ── Tiles to hold ───────────────────────────────────────────────────────

  /** The two months around today are always held, because the period pills of the left panel are computed from them. */
  const anchorTiles = () => tilesCovering(timeline, startOfMonth(addMonths(today, -1)), endOfMonth(addMonths(today, 1)));

  const viewportTiles = () => (viewport ? tilesToHold(timeline, viewport.firstDay, viewport.lastDay) : []);

  /** The tiles of the period the planner summarises, which can be far from the days on screen. */
  let summaryTiles = [];

  /** The tiles a page or refresh asks for: the anchor months and the viewport, within what one request may carry. */
  function pageTiles() {
    const center = viewport ? tileOf(timeline, viewport.centerDay ?? viewport.firstDay) : tileOf(timeline, today);
    const tiles = [...new Set([...anchorTiles(), ...viewportTiles()])].sort((a, b) => a - b);

    if (tiles.length <= LIMITS.maxTilesPerRequest) return tiles;
    return tiles
      .sort((a, b) => Math.abs(a - center) - Math.abs(b - center))
      .slice(0, LIMITS.maxTilesPerRequest)
      .sort((a, b) => a - b);
  }

  /** Windows for the tiles, in runs no longer than the server accepts. */
  function windowsFor(tiles) {
    const windows = [];
    for (const run of toRuns(tiles)) {
      for (let start = run.start; start <= run.end; start += LIMITS.maxWindowTiles) {
        windows.push(toIso(runToDays(timeline, { start, end: Math.min(run.end, start + LIMITS.maxWindowTiles - 1) })));
      }
    }
    return windows;
  }

  // ── The queue ───────────────────────────────────────────────────────────

  const refillTokens = () => {
    const now = clock();
    tokens = Math.min(settings.tokenCapacity, tokens + ((now - lastRefill) / 1000) * settings.tokensPerSecond);
    lastRefill = now;
  };

  /** Milliseconds to wait before the next task may start, 0 when it may start now. */
  function waitFor(task) {
    const now = clock();
    const blocked = Math.max(0, blockedUntil - now);
    if (task.kind !== "delta") return blocked;

    refillTokens();
    const forToken = tokens >= 1 ? 0 : Math.ceil(((1 - tokens) / settings.tokensPerSecond) * 1000);
    return Math.max(blocked, forToken);
  }

  const sleep = (ms) =>
    new Promise((resolve) => {
      sleeper = { resolve, timer: timers.setTimeout(resolve, ms) };
    });

  const nextTask = () => queue.reduce((best, task) => (!best || PRIORITY[task.kind] < PRIORITY[best.kind] ? task : best), null);

  function enqueue(task) {
    queue.push(task);

    // A task that arrives while the queue is waiting for the pace to allow the next one may not need to wait.
    if (sleeper) {
      timers.clearTimeout(sleeper.timer);
      sleeper.resolve();
    }
    pump();
  }

  async function pump() {
    if (pumping || disposed) return;
    pumping = true;

    try {
      while (queue.length > 0 && !disposed) {
        const task = nextTask();
        const wait = waitFor(task);
        if (wait > 0) {
          // The queue may change while waiting (a viewport change replaces the planned requests), so look again after.
          await sleep(wait);
          sleeper = null;
          continue;
        }

        queue = queue.filter((candidate) => candidate !== task);
        refillTokens();
        tokens = Math.max(0, tokens - 1);

        runningController = new AbortController();
        stats[task.kind] += 1;
        try {
          await task.run(runningController.signal, task);
        } finally {
          runningController = null;
        }
      }
    } finally {
      pumping = false;
      const waiters = idleWaiters;
      idleWaiters = [];
      waiters.forEach((resolve) => resolve());
    }
  }

  function abortRunning() {
    runningController?.abort();
  }

  const isStale = (taskEpoch) => disposed || taskEpoch !== epoch;

  // ── Failures ────────────────────────────────────────────────────────────

  function handleFailure(error, retry) {
    const waitSeconds = retryAfterSeconds(error);
    if (waitSeconds) {
      blockedUntil = clock() + waitSeconds * 1000;
      retry(waitSeconds * 1000);
      return;
    }

    if (!failureNotified) {
      failureNotified = true;
      notify.error?.("Failed to load planner data.");
    }
    retry(settings.failureRetryMs);
  }

  // ── Pages ───────────────────────────────────────────────────────────────

  function enqueuePage({ pageNumber, replace }) {
    const taskEpoch = epoch;
    const tiles = pageTiles();
    const { filters } = get();

    enqueue({
      kind: "page",
      async run(signal, task) {
        try {
          const { page, pagination } = await api.getPage(
            { pageNumber, pageSize: settings.pageSize, projectId: filters.projectId, resourceIds: filters.resourceIds, windows: windowsFor(tiles) },
            signal
          );
          if (isStale(taskEpoch)) {
            stats.discarded += 1;
            return;
          }

          failureNotified = false;
          store.setState((state) => ({
            ...state,
            data: applyPage(state.data, page, { replace, tiles }),
            status: "ready",
            error: null,
            isLoadingMore: false,
            hasMore: pagination ? Boolean(pagination.HasNext) : (page.resources || []).length === settings.pageSize,
            totalCount: pagination?.TotalCount ?? state.totalCount,
            pageNumber,
          }));
          scheduleCoverage();
        } catch (error) {
          if (isCanceled(error) || isStale(taskEpoch)) return;
          if (retryWhenAllowed(error, task)) return;

          notify.error?.("Failed to load planner data.");
          set({ status: replace ? "error" : get().status, error, isLoadingMore: false });
        }
      },
    });
  }

  /** When the server asked to slow down, puts the task back to run again once the wait is over. */
  function retryWhenAllowed(error, task) {
    const waitSeconds = retryAfterSeconds(error);
    if (!waitSeconds) return false;

    blockedUntil = clock() + waitSeconds * 1000;
    enqueue(task);
    return true;
  }

  /** Starts over with the given filters: a new first page, and nothing from before is kept or accepted any more. */
  function start(filters) {
    epoch += 1;
    abortRunning();
    queue = [];
    optimisticEdits.clear();
    stale.clear();
    failureNotified = false;

    set({ ...initialState(), filters, status: "loading", epoch });
    enqueuePage({ pageNumber: 1, replace: true });
  }

  const normalizeFilters = (filters = {}) => ({ projectId: filters.projectId || null, resourceIds: [...(filters.resourceIds || [])] });

  // ── Coverage ────────────────────────────────────────────────────────────

  function planAndEnqueueCoverage() {
    coverageTimer = null;
    lastCoverageAt = clock();
    if (disposed || !viewport || get().status !== "ready") return;

    // Requests that have not started are made again from the current viewport.
    const dropped = queue.filter((task) => task.kind === "delta");
    queue = queue.filter((task) => task.kind !== "delta");
    for (const task of dropped) setData((data) => clearInflight(data, task.resourceIds, task.tiles));

    const { data, filters } = get();
    const resources = viewport.resourceIds
      .filter((id) => data.allocs[id])
      .map((id) => ({ id, covered: data.allocs[id].tiles, inflight: data.allocs[id].inflight }));
    const wanted = [...new Set([...tilesToHold(timeline, viewport.firstDay, viewport.lastDay), ...summaryTiles])].sort((a, b) => a - b);
    const center = tileOf(timeline, viewport.centerDay ?? viewport.firstDay);

    for (const request of planCoverage(timeline, resources, wanted, center)) {
      setData((current) => markInflight(current, request.resourceIds, request.tiles));
      enqueueDelta(request, filters.projectId);
    }
  }

  function enqueueDelta(request, projectId) {
    const taskEpoch = epoch;
    const { resourceIds, tiles } = request;

    enqueue({
      kind: "delta",
      resourceIds,
      tiles,
      async run(signal) {
        try {
          const result = await api.getAllocations(
            { resourceIds, windows: request.windows.map(toIso), knownRanges: request.knownRanges.map(toIso), projectId },
            signal
          );
          if (isStale(taskEpoch)) {
            stats.discarded += 1;
            return;
          }

          failureNotified = false;
          setData((data) => applyDelta(data, result, { resourceIds, tiles }));
        } catch (error) {
          if (isStale(taskEpoch)) return;
          setData((data) => clearInflight(data, resourceIds, tiles));
          if (isCanceled(error)) return;
          handleFailure(error, (delay) => scheduleCoverage(delay));
        }
      },
    });
  }

  /** Plans the coverage soon, but at most once per throttle period however fast the viewport moves. */
  function scheduleCoverage(delay) {
    if (disposed || coverageTimer !== null) return;

    const sinceLast = clock() - lastCoverageAt;
    const wait = Math.max(delay ?? 0, settings.coverageThrottleMs - sinceLast, 0);
    coverageTimer = timers.setTimeout(planAndEnqueueCoverage, wait);
  }

  // ── Refresh ─────────────────────────────────────────────────────────────

  /** Refreshes the stale resources that are on screen; the others wait until they come into view. */
  function refreshStaleVisible() {
    if (!viewport || stale.size === 0) return;

    const due = viewport.resourceIds.filter((id) => stale.has(id));
    for (const id of due) stale.delete(id);
    if (due.length > 0) refreshResources(due);
  }

  /** Marks everything held as possibly out of date, for a change made elsewhere (a task was saved). */
  function invalidateAll() {
    for (const id of get().data.order) stale.add(id);
    refreshStaleVisible();
  }

  function refreshResources(resourceIds) {
    const { data, filters } = get();
    const ids = [...new Set(resourceIds)].filter((id) => data.allocs[id]);
    for (const id of ids) stale.delete(id);
    const taskEpoch = epoch;

    for (let offset = 0; offset < ids.length; offset += settings.refreshSize) {
      const chunk = ids.slice(offset, offset + settings.refreshSize);
      const tiles = pageTiles();

      enqueue({
        kind: "refresh",
        async run(signal, task) {
          try {
            const { page } = await api.getPage(
              { pageNumber: 1, pageSize: settings.refreshSize, projectId: filters.projectId, resourceIds: chunk, windows: windowsFor(tiles) },
              signal
            );
            if (isStale(taskEpoch)) return;
            setData((current) => replaceResources(current, page, { tiles }));
            scheduleCoverage();
          } catch (error) {
            if (isCanceled(error) || isStale(taskEpoch)) return;
            if (retryWhenAllowed(error, task)) return;

            notify.error?.("Failed to refresh planner data.");
          }
        },
      });
    }
  }

  // ── Editing ─────────────────────────────────────────────────────────────

  /**
   * Moves an allocation: the bar moves at once and the change is sent to the server. If the server refuses, or
   * reports a conflict, only this allocation goes back to where it was.
   */
  async function updateAllocation(resourceId, taskId, startDate, endDate) {
    const previous = getAllocation(get().data, resourceId, taskId);
    if (!previous) return;

    const editKey = `${resourceId}:${taskId}`;
    const optimistic = { startDate, endDate };
    optimisticEdits.set(editKey, optimistic);
    setData((data) => patchAllocation(data, resourceId, taskId, optimistic));

    const taskEpoch = epoch;

    // A newer edit of the same allocation may have been made while this one was on its way; this one then no
    // longer has a say in how the allocation looks.
    const isLatest = () => !isStale(taskEpoch) && optimisticEdits.get(editKey) === optimistic;

    const rollback = () => {
      if (!isLatest()) return;
      setData((data) => patchAllocation(data, resourceId, taskId, { startDate: previous.startDate, endDate: previous.endDate }));
    };

    try {
      const response = await api.updateAllocation(taskId, resourceId, { startDate, endDate });
      const { value, failure } = unwrapResult(response?.data);

      if (failure) {
        rollback();
        notify.error?.(failure.message || "Failed to update allocation. Changes reverted.");
      } else if (value && value.isPermissible === false) {
        rollback();
        notify.conflicts?.(value.conflicts || []);
      } else if (!isStale(taskEpoch)) {
        // The server recomputes the hours for the new dates, so what it answers replaces the optimistic values.
        if (isLatest() && value?.startDate && value?.endDate) {
          setData((data) =>
            patchAllocation(data, resourceId, taskId, {
              startDate: value.startDate,
              endDate: value.endDate,
              hoursPerDay: value.hoursPerDay ?? previous.hoursPerDay,
              totalHours: value.totalHours ?? previous.totalHours,
              includeWeekends: value.isIncludeWeekends ?? previous.includeWeekends,
            })
          );
        }
        notify.saved?.();
      }
    } catch (error) {
      rollback();
      notify.error?.("Failed to update allocation. Changes reverted.");
    } finally {
      if (optimisticEdits.get(editKey) === optimistic) optimisticEdits.delete(editKey);
    }
  }

  // ── Public ──────────────────────────────────────────────────────────────

  return {
    stats,

    /** Loads the first page for the filters, forgetting everything held before. */
    setFilters(filters) {
      start(normalizeFilters(filters));
    },

    reload() {
      start(get().filters);
    },

    loadNextPage() {
      const { status, hasMore, isLoadingMore, pageNumber } = get();
      if (status !== "ready" || !hasMore || isLoadingMore) return;

      set({ isLoadingMore: true });
      enqueuePage({ pageNumber: pageNumber + 1, replace: false });
    },

    /** Tells the controller what is on screen: the days, the middle day and the resources whose rows are visible. */
    setViewport(next) {
      viewport = next;
      if (get().status !== "ready") return;

      refreshStaleVisible();
      scheduleCoverage();
    },

    /**
     * Tells the controller which days the planner summarises. The resources on screen get all of them, not only the
     * days around the viewport, because the hours and the tasks counted for the period come from what is held.
     */
    setSummaryRange(range) {
      const tiles = range ? tilesCovering(timeline, range.startDay, range.endDay) : [];
      if (tiles.length === summaryTiles.length && tiles.every((tile, index) => tile === summaryTiles[index])) return;

      summaryTiles = tiles;
      if (get().status === "ready") scheduleCoverage();
    },

    refreshResources,
    invalidateAll,
    updateAllocation,

    /** Resolves once every request that is queued or running has finished. */
    idle() {
      if (!pumping && queue.length === 0) return Promise.resolve();
      return new Promise((resolve) => idleWaiters.push(resolve));
    },

    dispose() {
      disposed = true;
      abortRunning();
      queue = [];
      if (coverageTimer !== null) timers.clearTimeout(coverageTimer);
      if (sleeper) {
        timers.clearTimeout(sleeper.timer);
        sleeper.resolve();
      }
      coverageTimer = null;
    },
  };
}
